import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import "./onboarding.css";

// ── Role definitions ────────────────────────────────────────────────────────
const ROLES = [
  { id: "us_physician",   label: "US Physician",                  icon: "fa-user-md",      impliedCountry: "US" },
  { id: "intl_physician", label: "International Physician",       icon: "fa-globe",        impliedCountry: null },
  { id: "nurse_pa_np",    label: "Nurse / PA / NP",               icon: "fa-stethoscope",  impliedCountry: null },
  { id: "student",        label: "Medical Student / Resident",    icon: "fa-graduation-cap", impliedCountry: null },
  { id: "researcher",     label: "Researcher / Academic",         icon: "fa-flask",        impliedCountry: null },
  { id: "other",          label: "Other Healthcare Professional", icon: "fa-heart-pulse",  impliedCountry: null },
];

// ── Country list ────────────────────────────────────────────────────────────
const COUNTRIES = [
  { code: "US", name: "United States" },
  { code: "GB", name: "United Kingdom" },
  { code: "AU", name: "Australia" },
  { code: "CA", name: "Canada" },
  { code: "IN", name: "India" },
  { code: "DE", name: "Germany" },
  { code: "FR", name: "France" },
  { code: "NL", name: "Netherlands" },
  { code: "IE", name: "Ireland" },
  { code: "NZ", name: "New Zealand" },
  { code: "SG", name: "Singapore" },
  { code: "ZA", name: "South Africa" },
  { code: "NG", name: "Nigeria" },
  { code: "KE", name: "Kenya" },
  { code: "AE", name: "United Arab Emirates" },
  { code: "SA", name: "Saudi Arabia" },
  { code: "PK", name: "Pakistan" },
  { code: "PH", name: "Philippines" },
  { code: "BR", name: "Brazil" },
  { code: "MX", name: "Mexico" },
  { code: "JP", name: "Japan" },
  { code: "CN", name: "China" },
  { code: "KR", name: "South Korea" },
  { code: "OTHER", name: "Other" },
];

// ── Identifier config per country ───────────────────────────────────────────
const IDENTIFIER_CONFIG = {
  US: {
    label: "NPI (National Provider Identifier)",
    placeholder: "10-digit NPI",
    patternHint: "Must be exactly 10 digits",
    validate: /^\d{10}$/,
    optional: false,
    // Live NPPES lookup: returns { valid, providerName, specialty }
    lookup: async (value) => {
      try {
        const res = await fetch(
          `https://npiregistry.cms.hhs.gov/api/?number=${value}&version=2.1`
        );
        const data = await res.json();
        if (data.result_count === 1) {
          const r = data.results[0];
          const name = r.basic?.credential
            ? `${r.basic.first_name} ${r.basic.last_name}, ${r.basic.credential}`
            : `${r.basic?.first_name} ${r.basic?.last_name}`;
          const specialty = r.taxonomies?.[0]?.desc ?? "";
          return { valid: true, providerName: name.trim(), specialty };
        }
        return { valid: false };
      } catch {
        return { valid: false, error: "Could not reach the NPI registry." };
      }
    },
  },
  GB: {
    label: "GMC Reference Number",
    placeholder: "7-digit GMC number",
    patternHint: "Must be exactly 7 digits",
    validate: /^\d{7}$/,
    optional: true,
    lookup: null,
  },
  AU: {
    label: "AHPRA Registration Number",
    placeholder: "e.g. MED0001234567",
    patternHint: "3 letters followed by 10 digits",
    validate: /^[A-Z]{3}\d{10}$/,
    optional: true,
    lookup: null,
  },
  CA: {
    label: "Provincial License Number",
    placeholder: "Enter your license number",
    patternHint: null,
    validate: null,
    optional: true,
    lookup: null,
  },
  IN: {
    label: "Medical Registration Number (MCI/NMC)",
    placeholder: "Enter your registration number",
    patternHint: null,
    validate: null,
    optional: true,
    lookup: null,
  },
  NL: {
    label: "BIG-number",
    placeholder: "11-digit BIG number",
    patternHint: "Must be exactly 11 digits",
    validate: /^\d{11}$/,
    optional: true,
    lookup: null,
  },
  IE: {
    label: "IMC Registration Number",
    placeholder: "Enter your IMC number",
    patternHint: null,
    validate: null,
    optional: true,
    lookup: null,
  },
  NZ: {
    label: "MCNZ Registration Number",
    placeholder: "Enter your MCNZ number",
    patternHint: null,
    validate: null,
    optional: true,
    lookup: null,
  },
  SG: {
    label: "MCR Number (Singapore Medical Council)",
    placeholder: "e.g. M12345A",
    patternHint: null,
    validate: null,
    optional: true,
    lookup: null,
  },
  ZA: {
    label: "HPCSA Registration Number",
    placeholder: "Enter your HPCSA number",
    patternHint: null,
    validate: null,
    optional: true,
    lookup: null,
  },
  DEFAULT: {
    label: "Medical License / Registration Number",
    placeholder: "Enter your license number (optional)",
    patternHint: null,
    validate: null,
    optional: true,
    lookup: null,
  },
};

// Roles that skip the identifier step entirely
const ROLES_WITHOUT_IDENTIFIER = ["researcher", "other"];

// ── Helpers ─────────────────────────────────────────────────────────────────
function getIdentifierConfig(role, countryCode) {
  if (ROLES_WITHOUT_IDENTIFIER.includes(role)) return null;
  return IDENTIFIER_CONFIG[countryCode] ?? IDENTIFIER_CONFIG.DEFAULT;
}

function saveOnboardingLocally(userSub, profile) {
  localStorage.setItem(
    `onboarding_${userSub}`,
    JSON.stringify({ ...profile, completedAt: new Date().toISOString() })
  );
}

// ── Component ────────────────────────────────────────────────────────────────
export default function Onboarding() {
  const { user } = useAuth0();
  const navigate = useNavigate();

  const [step, setStep] = useState(1);          // 1=role, 2=country, 3=identifier
  const [role, setRole] = useState(null);
  const [country, setCountry] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [identifier, setIdentifier] = useState("");
  const [lookupResult, setLookupResult] = useState(null); // { valid, providerName, specialty }
  const [lookupError, setLookupError] = useState("");
  const [validating, setValidating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const selectedRole = ROLES.find((r) => r.id === role);
  const effectiveCountry = selectedRole?.impliedCountry ?? country;
  const idConfig = getIdentifierConfig(role, effectiveCountry);

  // ── Step 1: select role ───────────────────────────────────────────────────
  function handleRoleSelect(roleId) {
    setRole(roleId);
    const r = ROLES.find((x) => x.id === roleId);
    if (r.impliedCountry) {
      // US Physician → skip country step
      setCountry(r.impliedCountry);
      setStep(3);
    } else {
      setStep(2);
    }
  }

  // ── Step 2: select country ────────────────────────────────────────────────
  function handleCountryContinue() {
    if (!country) return;
    const cfg = getIdentifierConfig(role, country);
    if (!cfg) {
      // Role doesn't need an identifier — finish immediately
      handleComplete(country, "");
    } else {
      setStep(3);
    }
  }

  // ── Step 3: validate identifier ───────────────────────────────────────────
  async function handleIdentifierLookup() {
    if (!idConfig?.lookup) return;
    setValidating(true);
    setLookupResult(null);
    setLookupError("");
    const result = await idConfig.lookup(identifier);
    setValidating(false);
    if (result.valid) {
      setLookupResult(result);
    } else {
      setLookupError(result.error ?? "NPI not found in the registry. Check your number and try again.");
    }
  }

  function identifierPassesValidation() {
    if (!idConfig) return true;
    if (!identifier && idConfig.optional) return true;
    if (!identifier && !idConfig.optional) return false;
    if (idConfig.validate && !idConfig.validate.test(identifier)) return false;
    // For US NPI, also require a successful lookup
    if (idConfig.lookup && !lookupResult?.valid) return false;
    return true;
  }

  // ── Final save ────────────────────────────────────────────────────────────
  async function handleComplete(overrideCountry, overrideIdentifier) {
    setSubmitting(true);
    const profile = {
      role,
      country: overrideCountry ?? effectiveCountry,
      identifierType: idConfig?.label ?? null,
      identifier: overrideIdentifier ?? identifier,
      providerName: lookupResult?.providerName ?? null,
      specialty: lookupResult?.specialty ?? null,
    };

    // Persist locally so the gate in App.js can read it immediately
    saveOnboardingLocally(user.sub, profile);

    // Fire-and-forget to backend (best effort)
    try {
      await fetch("/api/user/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });
    } catch {
      // Non-blocking — local storage is the source of truth for now
    }

    setSubmitting(false);
    navigate("/");
  }

  // ── Filtered country list ─────────────────────────────────────────────────
  const filteredCountries = COUNTRIES.filter((c) =>
    c.name.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // ── Render ────────────────────────────────────────────────────────────────
  const totalSteps = selectedRole?.impliedCountry
    ? 2   // role → identifier (country implicit)
    : ROLES_WITHOUT_IDENTIFIER.includes(role)
    ? 2   // role → country (no identifier)
    : 3;  // role → country → identifier

  const currentStepDisplay = step === 3 && selectedRole?.impliedCountry ? 2 : step;

  return (
    <div className="onboarding-container">
      <div className="onboarding-box">
        {/* Header */}
        <div className="onboarding-header">
          <h2>Set up your profile</h2>
          <p className="onboarding-subtitle">
            This helps us tailor your experience as a medical professional.
          </p>
          <div className="step-indicator">
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`step-dot ${i + 1 <= currentStepDisplay ? "active" : ""}`}
              />
            ))}
          </div>
        </div>

        {/* ── Step 1: Role ── */}
        {step === 1 && (
          <div className="onboarding-step">
            <h3>What best describes your role?</h3>
            <div className="role-grid">
              {ROLES.map((r) => (
                <button
                  key={r.id}
                  className={`role-card ${role === r.id ? "selected" : ""}`}
                  onClick={() => handleRoleSelect(r.id)}
                >
                  <i className={`fas ${r.icon} role-icon`} />
                  <span>{r.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step 2: Country ── */}
        {step === 2 && (
          <div className="onboarding-step">
            <h3>Which country are you based in?</h3>
            <input
              className="onboarding-input"
              type="text"
              placeholder="Search countries..."
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
              autoFocus
            />
            <div className="country-list">
              {filteredCountries.map((c) => (
                <button
                  key={c.code}
                  className={`country-option ${country === c.code ? "selected" : ""}`}
                  onClick={() => setCountry(c.code)}
                >
                  {country === c.code && <i className="fas fa-check check-icon" />}
                  {c.name}
                </button>
              ))}
            </div>
            <div className="onboarding-actions">
              <button className="btn-back" onClick={() => { setStep(1); setCountry(""); }}>
                Back
              </button>
              <button
                className="btn-primary"
                disabled={!country}
                onClick={handleCountryContinue}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Identifier ── */}
        {step === 3 && idConfig && (
          <div className="onboarding-step">
            <h3>{idConfig.label}</h3>
            {idConfig.optional && (
              <p className="onboarding-hint">
                This is optional, but helps us personalise your experience.
              </p>
            )}

            <div className="identifier-row">
              <input
                className="onboarding-input"
                type="text"
                placeholder={idConfig.placeholder}
                value={identifier}
                onChange={(e) => {
                  setIdentifier(e.target.value.toUpperCase());
                  setLookupResult(null);
                  setLookupError("");
                }}
                autoFocus
              />
              {idConfig.lookup && (
                <button
                  className="btn-verify"
                  disabled={!identifier || validating}
                  onClick={handleIdentifierLookup}
                >
                  {validating ? "Verifying..." : "Verify"}
                </button>
              )}
            </div>

            {idConfig.patternHint && !lookupResult && !lookupError && (
              <p className="onboarding-hint">{idConfig.patternHint}</p>
            )}

            {lookupError && (
              <p className="onboarding-error">{lookupError}</p>
            )}

            {lookupResult?.valid && (
              <div className="lookup-success">
                <i className="fas fa-circle-check" />
                <div>
                  <strong>{lookupResult.providerName}</strong>
                  {lookupResult.specialty && (
                    <span className="lookup-specialty">{lookupResult.specialty}</span>
                  )}
                </div>
              </div>
            )}

            <div className="onboarding-actions">
              <button
                className="btn-back"
                onClick={() => {
                  setIdentifier("");
                  setLookupResult(null);
                  setLookupError("");
                  setStep(selectedRole?.impliedCountry ? 1 : 2);
                }}
              >
                Back
              </button>
              {idConfig.optional && (
                <button
                  className="btn-skip"
                  onClick={() => handleComplete(undefined, "")}
                >
                  Skip for now
                </button>
              )}
              <button
                className="btn-primary"
                disabled={!identifierPassesValidation() || submitting}
                onClick={() => handleComplete()}
              >
                {submitting ? "Saving..." : "Complete Setup"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
